import type { LeadWithValuation } from '@/types'

// ─── Lead Scoring (1-5 stelle) ───────────────────────────────────────────────
export function computeLeadScore(lead: LeadWithValuation): number {
  let score = 0
  if (lead.phone) score++
  if (lead.valuation && lead.valuation.estimated_value > 300000) score++
  if (lead.valuation?.address && lead.valuation?.area) score++
  if (lead.source === 'ai_chat') score++
  if (lead.marketing_consent) score++
  return score
}

export function scoreLabel(score: number): string {
  if (score >= 5) return 'Eccellente'
  if (score >= 4) return 'Alto'
  if (score >= 3) return 'Medio'
  if (score >= 2) return 'Basso'
  return 'Minimo'
}

export function scoreColor(score: number): string {
  if (score >= 4) return 'text-amber-500'
  if (score >= 3) return 'text-amber-400'
  if (score >= 2) return 'text-gray-400'
  return 'text-gray-300'
}

// ─── WhatsApp ────────────────────────────────────────────────────────────────
export function buildWhatsAppUrl(
  phone: string,
  leadName: string,
  zoneName?: string
): string {
  // Normalizza numero: rimuovi spazi, aggiungi prefisso italiano se manca
  let cleaned = phone.replace(/[\s\-()]/g, '')
  if (cleaned.startsWith('0')) {
    cleaned = '39' + cleaned
  } else if (!cleaned.startsWith('+') && !cleaned.startsWith('39')) {
    cleaned = '39' + cleaned
  }
  cleaned = cleaned.replace(/^\+/, '')

  const nome = leadName || 'Gentile cliente'
  const zonaText = zoneName ? ` per la zona ${zoneName}` : ''
  const message = `Buongiorno ${nome}, contattiamo dal servizio Valutazioni Immobiliari Online. Ho visto la sua richiesta di valutazione${zonaText}. Posso esserle utile?`

  return `https://wa.me/${cleaned}?text=${encodeURIComponent(message)}`
}

// ─── Template Messaggi ───────────────────────────────────────────────────────
export function fillTemplate(
  template: string,
  lead: LeadWithValuation
): string {
  const nome = [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'Gentile cliente'
  const zona = lead.valuation?.zone_name_csv || 'N/A'
  const valore = lead.valuation?.estimated_value
    ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(lead.valuation.estimated_value)
    : 'N/A'
  const indirizzo = lead.valuation?.address || 'N/A'

  return template
    .replace(/\{\{nome\}\}/g, nome)
    .replace(/\{\{zona\}\}/g, zona)
    .replace(/\{\{valore\}\}/g, valore)
    .replace(/\{\{indirizzo\}\}/g, indirizzo)
    .replace(/\{\{email\}\}/g, lead.email)
    .replace(/\{\{telefono\}\}/g, lead.phone || 'N/A')
}

// ─── Callback helpers ────────────────────────────────────────────────────────
export function isCallbackOverdue(callbackDate: string): boolean {
  return new Date(callbackDate) < new Date()
}

export function isCallbackToday(callbackDate: string): boolean {
  const cb = new Date(callbackDate)
  const now = new Date()
  return (
    cb.getFullYear() === now.getFullYear() &&
    cb.getMonth() === now.getMonth() &&
    cb.getDate() === now.getDate()
  )
}

export function formatCallbackDate(dateString: string): string {
  const d = new Date(dateString)
  const now = new Date()
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()

  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const isTomorrow =
    d.getFullYear() === tomorrow.getFullYear() &&
    d.getMonth() === tomorrow.getMonth() &&
    d.getDate() === tomorrow.getDate()

  const time = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })

  if (isToday) return `Oggi alle ${time}`
  if (isTomorrow) return `Domani alle ${time}`
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) + ` alle ${time}`
}

// ─── Tag presets ─────────────────────────────────────────────────────────────
export const TAG_PRESETS = [
  { label: 'Investitore', color: 'bg-purple-100 text-purple-700' },
  { label: 'Prima casa', color: 'bg-blue-100 text-blue-700' },
  { label: 'Straniero', color: 'bg-indigo-100 text-indigo-700' },
  { label: 'Urgente', color: 'bg-red-100 text-red-700' },
  { label: 'Vendita rapida', color: 'bg-orange-100 text-orange-700' },
  { label: 'Lusso', color: 'bg-amber-100 text-amber-700' },
  { label: 'Ristrutturazione', color: 'bg-teal-100 text-teal-700' },
  { label: 'Affitto', color: 'bg-cyan-100 text-cyan-700' },
] as const

export function getTagColor(tag: string): string {
  const preset = TAG_PRESETS.find((p) => p.label.toLowerCase() === tag.toLowerCase())
  return preset?.color || 'bg-gray-100 text-gray-700'
}
